import { useEffect, useState } from "react";
import moment from "moment";

import { getSessionToken } from "@/api/auth";
import { AdminAccount, AdminVisit, SigninEntry, getAccountVisits, getSigninHistory, setAccountPermissions } from "@/api/admin";
import { ADVANCED_PERMISSIONS, APP_PERMISSIONS, PermissionInfo, otherPermissions } from "./permissionList";
import { Pill, ago, deviceLabel, when } from "./parts";

// ADM-3/6 — the side panel for one account: details, "Log in as", app switches, and recent
// admin activity. Each switch saves on its own; the server records every change.

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <span className="text-gray-500 dark:text-white/50">{label}</span>
    <span className="text-right text-gray-900 dark:text-white">{children}</span>
  </div>
);

const Switch = ({ on, disabled, onClick, label }: { on: boolean; disabled?: boolean; onClick: () => void; label: string }) => (
  // no type="button": the app's old snow.css makes every [type="button"] background transparent
  <button
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className={`relative inline-flex h-5 w-9 flex-none items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${on ? "bg-emerald-500" : "bg-gray-300 dark:bg-white/20"}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`} />
  </button>
);

// one line per visit (start paired with its end by the start of the pass) or permission change
const activityLines = (entries: AdminVisit[]) => {
  const ends: Record<string, string> = {};
  entries.filter((e) => e.action === "login-as:end" && e.pass).forEach((e) => (ends[e.pass as string] = e.at));
  return entries
    .filter((e) => e.action === "login-as:start" || e.action === "permissions:change")
    .map((e) => {
      const when = moment(e.at).format("MMM D, h:mma");
      const who = e.staffEmail || "Staff";
      if (e.action === "permissions:change") {
        const changes = [...(e.added || []).map((p) => `+${p}`), ...(e.removed || []).map((p) => `−${p}`)].join(" ");
        return `${who} changed apps (${changes}) · ${when}`;
      }
      const end = e.pass ? ends[e.pass] : undefined;
      const length = end ? ` · ${Math.max(1, Math.round(moment(end).diff(moment(e.at), "minutes", true)))} min` : "";
      return `${who} logged in as them · ${when}${length}`;
    });
};

export const AccountPanel = ({
  account,
  onClose,
  onLogInAs,
  opening,
  isOwner,
  onChanged,
}: {
  account: AdminAccount;
  onClose: () => void;
  onLogInAs?: () => void;
  opening: boolean;
  isOwner: boolean;
  onChanged: (account: AdminAccount) => void;
}) => {
  const [visits, setVisits] = useState<string[] | null>(null);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [signins, setSignins] = useState<{ activeDays: string[]; signIns: SigninEntry[] } | null>(null);

  const loadActivity = () =>
    getAccountVisits({ token: getSessionToken(), accountId: account.id })
      .then((entries) => setVisits(activityLines(entries)))
      .catch(() => setVisits([]));

  useEffect(() => {
    setVisits(null);
    setError("");
    loadActivity();
    setSignins(null);
    getSigninHistory({ token: getSessionToken(), accountId: account.id })
      .then(setSignins)
      .catch(() => setSignins({ activeDays: [], signIns: [] }));
  }, [account.id]);

  const has = (id: string) => (id === "admin" ? account.isAdmin : account.permissions.includes(id));

  const toggle = async (id: string) => {
    if (saving) return;
    setSaving(id);
    setError("");
    const turningOn = !has(id);
    try {
      const { permissions } = await setAccountPermissions({ token: getSessionToken(), accountId: account.id, add: turningOn ? [id] : [], remove: turningOn ? [] : [id] });
      onChanged({ ...account, permissions: permissions.filter((p) => p !== "admin"), isAdmin: permissions.includes("admin") });
      loadActivity();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save that change. Try again.");
    }
    setSaving("");
  };

  const SwitchRow = ({ p }: { p: PermissionInfo }) => (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div className="text-sm">
        <div className="text-gray-900 dark:text-white">{p.label}</div>
        {p.note && <div className="text-xs text-gray-500 dark:text-white/50">{p.note}</div>}
      </div>
      <Switch on={has(p.id)} disabled={Boolean(saving)} onClick={() => toggle(p.id)} label={p.label} />
    </div>
  );

  const others = otherPermissions(account.permissions).filter((p) => p !== account.app);

  return (
    <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col border-l border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900">
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-white/10">
        <div>
          <div className="flex flex-wrap items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
            {account.name}
            {account.isAdmin && <Pill label="Admin" tone="blue" />}
            {account.deactivated && <Pill label="Deactivated" tone="red" />}
          </div>
          <div className="text-sm text-gray-500 dark:text-white/60">
            {account.email}
            {account.company ? ` · ${account.company}` : ""}
          </div>
        </div>
        <button onClick={onClose} aria-label="Close" className="rounded-md px-2 py-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10">
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {onLogInAs ? (
          <button
            onClick={onLogInAs}
            disabled={opening}
            className="mb-2 w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-60 dark:bg-white dark:text-gray-900"
          >
            {opening ? "Opening…" : `Log in as ${account.name}`}
          </button>
        ) : (
          <p className="mb-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:bg-white/5 dark:text-white/60">
            {account.isAdmin ? "You can't log in as another admin." : "This account is deactivated."}
          </p>
        )}
        <p className="mb-5 text-xs text-gray-500 dark:text-white/50">They stay signed in. The visit lasts up to an hour and is recorded.</p>

        <div className="mb-5 divide-y divide-gray-100 dark:divide-white/5">
          <Row label="Signed up">{when(account.signedUp)}</Row>
          <Row label="Last active">{ago(account.lastActive)}</Row>
          {account.teamMemberOf && <Row label="Team member">Yes, of another account</Row>}
        </div>

        <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-white/50">Apps</h3>
        {error && <div className="mb-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-300">{error}</div>}
        <div className="mb-3 divide-y divide-gray-50 dark:divide-white/5">
          {APP_PERMISSIONS.map((p) => (
            <SwitchRow key={p.id} p={p} />
          ))}
        </div>

        <button onClick={() => setShowAdvanced(!showAdvanced)} className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 hover:text-gray-800 dark:text-white/50">
          {showAdvanced ? "▾" : "▸"} Advanced
        </button>
        {showAdvanced && (
          <div className="mb-3 divide-y divide-gray-50 dark:divide-white/5">
            {ADVANCED_PERMISSIONS.map((p) => (
              <SwitchRow key={p.id} p={p} />
            ))}
          </div>
        )}

        <div className="mb-3 flex items-start justify-between gap-3 border-t border-gray-100 pt-3 dark:border-white/10">
          <div className="text-sm">
            <div className="text-gray-900 dark:text-white">Admin</div>
            <div className="text-xs text-gray-500 dark:text-white/50">{isOwner ? "Can open this screen and log in as customers." : "🔒 Only Kyle can change this."}</div>
          </div>
          <Switch on={account.isAdmin} disabled={!isOwner || Boolean(saving)} onClick={() => toggle("admin")} label="Admin" />
        </div>

        {Boolean(others.length) && (
          <div className="mb-5">
            <div className="mb-1 text-xs text-gray-500 dark:text-white/50">Other settings on this account (kept as they are)</div>
            <div className="flex flex-wrap gap-1">
              {others.map((p) => (
                <Pill key={p} label={p} />
              ))}
            </div>
          </div>
        )}

        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-white/50">Sign-ins &amp; activity</h3>
        {signins === null && <p className="mb-5 text-sm text-gray-500">Loading…</p>}
        {signins && (
          <div className="mb-5 text-sm">
            <p className="mb-2 text-gray-700 dark:text-white/80">
              Active <b>{account.activeDays30}</b> of the last 30 days · <b>{account.signIns30}</b> sign-in{account.signIns30 === 1 ? "" : "s"}
              {account.failed30 > 0 && <span className="text-red-600 dark:text-red-400"> · {account.failed30} failed</span>}
            </p>
            {Boolean(signins.activeDays.length) && (
              <div className="mb-3 flex flex-wrap gap-1">
                {signins.activeDays.map((d) => (
                  <Pill key={d} label={moment(d).format("MMM D")} />
                ))}
              </div>
            )}
            {!signins.signIns.length && <p className="text-gray-500 dark:text-white/60">No sign-ins recorded yet.</p>}
            <ul className="space-y-1.5">
              {signins.signIns.map((x, i) => (
                <li key={i} className="leading-snug">
                  <span className={x.ok ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}>{x.ok ? "✓" : "✕"}</span>{" "}
                  <span className="text-gray-900 dark:text-white">{moment(x.at).format("MMM D, h:mma")}</span>
                  {!x.ok && x.reason && <span className="text-red-600 dark:text-red-400"> · {x.reason}</span>}
                  {x.method && x.method !== "password" && <span className="text-amber-700 dark:text-amber-300"> · {x.method === "master" ? "master password" : x.method}</span>}
                  <div className="text-xs text-gray-500 dark:text-white/50">
                    {deviceLabel(x.device)}
                    {x.ip ? ` · ${x.ip}` : ""}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-white/50">Recent admin activity</h3>
        {visits === null && <p className="text-sm text-gray-500">Loading…</p>}
        {visits && !visits.length && <p className="text-sm text-gray-500 dark:text-white/60">No admin activity yet.</p>}
        {visits && Boolean(visits.length) && (
          <ul className="space-y-1 text-sm text-gray-700 dark:text-white/80">
            {visits.map((v, i) => (
              <li key={i}>{v}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
